/**
 * Individual leaderboard page — fetches from `/public/leaderboard/:eventId/individual`,
 * displays ranked individual list, and updates in real-time via WebSocket.
 *
 * Requirements: 16.1 (public leaderboard), 16.2 (individual view), 16.3 (real-time updates)
 */

import { useParams, Link } from 'react-router-dom';
import { useState, useEffect, useCallback, useRef } from 'react';
import type { IndividualLeaderboardEntry } from '@scoring/shared';
import { usePublicApiClient } from '../hooks/usePublicApiClient.js';
import { useLeaderboardSocket } from '../hooks/useLeaderboardSocket.js';
import type { LeaderboardUpdateMessage } from '../hooks/useLeaderboardSocket.js';

export function IndividualLeaderboardPage(): JSX.Element {
  const { eventId } = useParams<{ eventId: string }>();
  const apiClient = usePublicApiClient();

  const [entries, setEntries] = useState<IndividualLeaderboardEntry[]>([]);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Track which participant IDs were recently updated for highlight animation.
  const [updatedParticipantIds, setUpdatedParticipantIds] = useState<Set<string>>(new Set());
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Initial data fetch.
  useEffect(() => {
    if (!eventId) return;

    let cancelled = false;

    async function fetchData(): Promise<void> {
      try {
        setLoading(true);
        setError(null);
        const response = await apiClient.request<{
          entries: IndividualLeaderboardEntry[];
          lastUpdated: string;
        }>('GET', `/public/leaderboard/${encodeURIComponent(eventId!)}/individual`, {
          public: true,
        });
        if (!cancelled) {
          setEntries(response.entries);
          setLastUpdated(response.lastUpdated);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load leaderboard');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void fetchData();
    return () => {
      cancelled = true;
    };
  }, [eventId, apiClient]);

  // Handle WebSocket updates.
  const handleUpdate = useCallback(
    (message: LeaderboardUpdateMessage) => {
      if (message.eventId !== eventId) return;

      // Determine which participants changed scores.
      const previousScores = new Map(entries.map((e) => [e.participantId, e.totalScore]));
      const changedIds = new Set<string>();
      for (const entry of message.individual) {
        const prev = previousScores.get(entry.participantId);
        if (prev !== undefined && prev !== entry.totalScore) {
          changedIds.add(entry.participantId);
        } else if (prev === undefined) {
          // New participant appeared.
          changedIds.add(entry.participantId);
        }
      }

      setEntries(message.individual);
      setLastUpdated(message.lastUpdated);

      // Trigger highlight animation for changed entries.
      if (changedIds.size > 0) {
        setUpdatedParticipantIds(changedIds);
        if (highlightTimeoutRef.current) {
          clearTimeout(highlightTimeoutRef.current);
        }
        highlightTimeoutRef.current = setTimeout(() => {
          setUpdatedParticipantIds(new Set());
          highlightTimeoutRef.current = null;
        }, 2000);
      }
    },
    [eventId, entries],
  );

  useLeaderboardSocket({
    eventId,
    onUpdate: handleUpdate,
    enabled: !loading && !error,
  });

  // Cleanup highlight timeout on unmount.
  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="leaderboard-container">
        <p className="loading-message">Loading individual leaderboard…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="leaderboard-container">
        <p className="error-message" role="alert">
          {error}
        </p>
      </div>
    );
  }

  return (
    <div className="leaderboard-container">
      <header className="leaderboard-header">
        <h1>Individual Leaderboard</h1>
        <nav aria-label="Leaderboard views">
          <Link to={`/${eventId}`} className="nav-link">
            Overview
          </Link>
          <Link to={`/${eventId}/team`} className="nav-link">
            Team
          </Link>
        </nav>
      </header>

      {lastUpdated && (
        <p className="last-updated">
          Last updated: {new Date(lastUpdated).toLocaleTimeString()}
        </p>
      )}

      {entries.length === 0 ? (
        <p className="empty-message">No individual scores recorded yet.</p>
      ) : (
        <table className="leaderboard-table" aria-label="Individual leaderboard rankings">
          <thead>
            <tr>
              <th scope="col">Rank</th>
              <th scope="col">Name</th>
              <th scope="col">Team</th>
              <th scope="col">Score</th>
              <th scope="col">Stations</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr
                key={entry.participantId}
                className={
                  updatedParticipantIds.has(entry.participantId)
                    ? 'leaderboard-row updated'
                    : 'leaderboard-row'
                }
              >
                <td className="rank-cell">{entry.rank}</td>
                <td className="name-cell">{entry.participantName}</td>
                <td className="team-cell">{entry.teamName ?? '—'}</td>
                <td className="score-cell">{entry.totalScore}</td>
                <td className="stations-cell">
                  {entry.stationsCompleted}/{entry.totalStations}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
